import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import {
  ConfiguracaoGeralDTO,
  ConfiguracaoGeralFormDTO,
} from '../../models/configuracoes/configuracao-geral.model';
import { ConfiguracaoGeralService } from '../../services/configuracoes/configuracao-geral.service';
import { TipoDespesaService } from '../../services/enums/tipo-despesa.service';
import { TipoDespesaDTO } from '../../models/tipo-despesa.model';

import { Moeda, MOEDA_OPTIONS } from '../../enums/moeda.enum';
import { TipoDeReceita } from '../../enums/TipoReceita.enum';
import { TipoPadraoNota } from '../../enums/TipoPadraoNota.enum';
import { TipoImprimir } from '../../enums/TipoImprimir.enum';
import { TipoDeDespesa, TIPOS_DESPESA_NAO_EXIBIR } from '../../enums/TipoDespesa.enum';

interface SelectOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-configuracao-geral',
  templateUrl: './configuracao-geral.component.html',
  styleUrl: './configuracao-geral.component.scss',
  providers: [MessageService],
})
export class ConfiguracaoGeralComponent implements OnInit, OnDestroy {
  /** Lista de configuracoes gerais (normalmente uma por empresa). */
  configuracoes: ConfiguracaoGeralDTO[] = [];
  loading = true;

  /** Flag de perfil master — controla visibilidade das acoes de escrita. */
  isMaster = false;

  /** Aba ativa: 0 = cadastro, 1 = lista. */
  activeTabIndex = 1;

  /** Estado do formulario (criar/editar). */
  form: ConfiguracaoGeralFormDTO = this.createEmptyForm();

  /** Registro original completo carregado ao editar (usado no round-trip do PUT). */
  private registroOriginal: ConfiguracaoGeralDTO | null = null;

  isFormSubmitted = false;
  submitting = false;

  // Dialog de exclusao
  deleteDialogVisible = false;
  deleteItemId: number | null = null;

  // Opcoes dos dropdowns baseados em enum
  moedaOptions = MOEDA_OPTIONS;
  vendaOptions: SelectOption[] = [];
  tipoNotaOptions: SelectOption[] = [];
  tipoImprimirOptions: SelectOption[] = [];

  // Opcoes de tipo de despesa (carregadas de /enums/tipoDespesa)
  tipoDespesaOptions: TipoDespesaDTO[] = [];
  private tipoDespesaMap = new Map<string, string>();

  private langChangeSub!: Subscription;

  constructor(
    private messageService: MessageService,
    private translate: TranslateService,
    private configuracaoGeralService: ConfiguracaoGeralService,
    private tipoDespesaService: TipoDespesaService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {}

  ngOnInit(): void {
    this.verificarPerfilMaster();
    this.buildEnumOptions();
    this.carregarTiposDespesa();
    this.carregar();

    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.buildEnumOptions();
    });
  }

  ngOnDestroy(): void {
    this.langChangeSub?.unsubscribe();
  }

  // ===========================================================================
  // Perfil master
  // ===========================================================================

  /**
   * Le a flag categoriaAcessoMaster do storage (session > local).
   * Mesmo padrao usado pelo masterAccessGuard e menu-lateral.
   */
  private verificarPerfilMaster(): void {
    if (!isPlatformBrowser(this.platformId)) {
      this.isMaster = false;
      return;
    }
    const valor =
      sessionStorage.getItem('categoriaAcessoMaster') ||
      localStorage.getItem('categoriaAcessoMaster');
    this.isMaster = valor === 'true';
  }

  // ===========================================================================
  // Dropdowns
  // ===========================================================================

  private buildEnumOptions(): void {
    this.vendaOptions = Object.values(TipoDeReceita).map((v) => ({
      value: v,
      label: this.translate.instant(`configuracao-geral.enums.tipo_receita.${v.toLowerCase()}`),
    }));

    this.tipoNotaOptions = Object.values(TipoPadraoNota).map((v) => ({
      value: v,
      label: this.translate.instant(`configuracao-geral.enums.tipo_nota.${v.toLowerCase()}`),
    }));

    this.tipoImprimirOptions = Object.values(TipoImprimir).map((v) => ({
      value: v,
      label: this.translate.instant(`configuracao-geral.enums.tipo_imprimir.${v.toLowerCase()}`),
    }));
  }

  private carregarTiposDespesa(): void {
    this.tipoDespesaService
      .get<any>('/enums/tipoDespesa', { size: 200, sort: 'nome,asc' })
      .subscribe({
        next: (res: any) => {
          const tipos: TipoDespesaDTO[] = Array.isArray(res) ? res : res?.content || [];
          this.tipoDespesaOptions = tipos.filter(
            (t) => !!t.enum && !TIPOS_DESPESA_NAO_EXIBIR.includes(t.enum as TipoDeDespesa),
          );
          this.tipoDespesaMap = new Map(this.tipoDespesaOptions.map((t) => [t.enum, t.nome]));
        },
        error: () => {
          this.tipoDespesaOptions = [];
          this.tipoDespesaMap = new Map();
        },
      });
  }

  // ===========================================================================
  // Tabela
  // ===========================================================================

  carregar(): void {
    this.loading = true;
    this.configuracaoGeralService.get('/configuracaoGeral', { size: 100 }).subscribe({
      next: (response) => {
        this.configuracoes = (response.content || []).map((c) => this.normalizarDTO(c));
        this.loading = false;
      },
      error: () => {
        this.configuracoes = [];
        this.loading = false;
      },
    });
  }

  /**
   * Extrai o nome do enum quando o backend serializa o campo como objeto
   * ({ enum, nome, ... }) via LocalizableEnumSerializer. Aceita tambem string
   * simples (retrocompatibilidade) e null/undefined.
   */
  private extrairEnum(valor: any): string | undefined {
    if (valor == null) return undefined;
    if (typeof valor === 'string') return valor;
    return valor.enum ?? undefined;
  }

  /**
   * Normaliza os campos de enum do DTO para string. O backend os envia como
   * objeto ({ enum, nome, ... }); a tela (tabela e dropdowns) trabalha com o
   * nome do enum como string.
   */
  private normalizarDTO(dto: ConfiguracaoGeralDTO): ConfiguracaoGeralDTO {
    return {
      ...dto,
      moedaPadrao: this.extrairEnum(dto.moedaPadrao),
      vendaAVista: this.extrairEnum(dto.vendaAVista),
      vendaAPrazo: this.extrairEnum(dto.vendaAPrazo),
      tipoPadraoNotaComum: this.extrairEnum(dto.tipoPadraoNotaComum),
      tipoPadraoNotaFiscal: this.extrairEnum(dto.tipoPadraoNotaFiscal),
      tipoPadraoImprimir: this.extrairEnum(dto.tipoPadraoImprimir),
      tipoDespesaEspecialPadrao: this.extrairEnum(dto.tipoDespesaEspecialPadrao),
    };
  }

  /** Rotulo legivel da moeda para exibicao na tabela. */
  moedaLabel(valor?: string): string {
    if (!valor) return '';
    const opcao = this.moedaOptions.find((m) => m.value === valor);
    return opcao ? opcao.label : valor;
  }

  /** Rotulo legivel do tipo de despesa para exibicao na tabela. */
  tipoDespesaLabel(valor?: string): string {
    if (!valor) return '';
    return this.tipoDespesaMap.get(valor) || valor;
  }

  formatarDataEHora(dataISO?: string): string {
    if (!dataISO) return '';
    const data = new Date(dataISO);
    if (isNaN(data.getTime())) return '';
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    const horas = String(data.getHours()).padStart(2, '0');
    const minutos = String(data.getMinutes()).padStart(2, '0');
    return `${dia}/${mes}/${ano} ${horas}:${minutos}`;
  }

  // ===========================================================================
  // Formulario
  // ===========================================================================

  private createEmptyForm(): ConfiguracaoGeralFormDTO {
    // Semeia TODOS os campos que o backend exige não-nulos (mesmos defaults da
    // entidade ConfiguracaoGeral), garantindo que o POST de "Nova configuração"
    // seja sempre válido mesmo que o usuário não toque nesses dropdowns.
    return {
      moedaPadrao: Moeda.GUARANI,
      vendaAVista: TipoDeReceita.VENDAS_A_VISTA,
      vendaAPrazo: TipoDeReceita.VENDAS_A_PRAZO,
      tipoPadraoNotaComum: TipoPadraoNota.VENDA_COMUM,
      tipoPadraoNotaFiscal: TipoPadraoNota.MANUAL,
      tipoPadraoImprimir: TipoImprimir.NADA_A_FAZER,
      tipoDespesaEspecialPadrao: TipoDeDespesa.RETENCOES_DE_IVA,
      exibirVideoDeBoasVindas: false,
    };
  }

  nova(): void {
    if (!this.isMaster) return;
    this.registroOriginal = null;
    this.form = this.createEmptyForm();
    this.isFormSubmitted = false;
    this.activeTabIndex = 0;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  editar(item: ConfiguracaoGeralDTO): void {
    if (!this.isMaster) return;
    this.registroOriginal = { ...item };
    this.form = {
      id: item.id,
      moedaPadrao: item.moedaPadrao,
      vendaAVista: item.vendaAVista,
      vendaAPrazo: item.vendaAPrazo,
      tipoPadraoNotaComum: item.tipoPadraoNotaComum,
      tipoPadraoNotaFiscal: item.tipoPadraoNotaFiscal,
      tipoPadraoImprimir: item.tipoPadraoImprimir,
      tipoDespesaEspecialPadrao: item.tipoDespesaEspecialPadrao,
      unidadeMedidaPadrao: item.unidadeMedidaPadrao,
      unidadeMedidaPadraoId: item.unidadeMedidaPadraoId,
      versao: item.versao,
      versaoApple: item.versaoApple,
      exibirVideoDeBoasVindas: item.exibirVideoDeBoasVindas ?? false,
      urlVideoDeBoasVidas: item.urlVideoDeBoasVidas,
    };
    this.isFormSubmitted = false;
    this.activeTabIndex = 0;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  limparForm(): void {
    this.registroOriginal = null;
    this.form = this.createEmptyForm();
    this.isFormSubmitted = false;
  }

  submit(): void {
    if (this.submitting || !this.isMaster) return;
    this.isFormSubmitted = true;

    if (!this.form.vendaAVista || !this.form.vendaAPrazo) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('configuracao-geral.texts.campos_obrigatorios'),
      });
      return;
    }

    // Monta o DTO COMPLETO (round-trip): preserva os campos do registro
    // original que o FormMapper do backend sobrescreve, alterando apenas os
    // campos editaveis desta tela.
    const body: ConfiguracaoGeralDTO = {
      ...(this.registroOriginal ?? ({} as ConfiguracaoGeralDTO)),
      moedaPadrao: this.form.moedaPadrao,
      vendaAVista: this.form.vendaAVista,
      vendaAPrazo: this.form.vendaAPrazo,
      tipoPadraoNotaComum: this.form.tipoPadraoNotaComum,
      tipoPadraoNotaFiscal: this.form.tipoPadraoNotaFiscal,
      tipoPadraoImprimir: this.form.tipoPadraoImprimir,
      tipoDespesaEspecialPadrao: this.form.tipoDespesaEspecialPadrao,
      versao: this.form.versao,
      versaoApple: this.form.versaoApple,
      exibirVideoDeBoasVindas: this.form.exibirVideoDeBoasVindas,
      urlVideoDeBoasVidas: this.form.urlVideoDeBoasVidas,
    };

    this.submitting = true;

    const request$ = this.form.id
      ? this.configuracaoGeralService.put(`/configuracaoGeral/${this.form.id}`, body)
      : this.configuracaoGeralService.post('/configuracaoGeral', body);

    request$.subscribe({
      next: () => {
        this.submitting = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.form.id
            ? this.translate.instant('configuracao-geral.texts.sucesso_editar')
            : this.translate.instant('configuracao-geral.texts.sucesso_adicionar'),
        });
        this.limparForm();
        this.activeTabIndex = 1;
        this.carregar();
      },
      error: (err) => {
        this.submitting = false;
        this.tratarErro(err, 'configuracao-geral.texts.erro_salvar');
      },
    });
  }

  // ===========================================================================
  // Exclusao
  // ===========================================================================

  confirmarExcluir(item: ConfiguracaoGeralDTO): void {
    if (!this.isMaster) return;
    this.deleteItemId = item.id;
    this.deleteDialogVisible = true;
  }

  excluir(): void {
    if (this.deleteItemId == null) return;

    this.configuracaoGeralService
      .delete(`/configuracaoGeral/${this.deleteItemId}`)
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('common.sucesso'),
            detail: this.translate.instant('configuracao-geral.texts.sucesso_excluir'),
          });
          this.deleteDialogVisible = false;
          this.deleteItemId = null;
          this.carregar();
        },
        error: (err) => {
          this.deleteDialogVisible = false;
          this.deleteItemId = null;
          this.tratarErro(err, 'configuracao-geral.texts.erro_excluir');
        },
      });
  }

  // ===========================================================================
  // Tratamento de erro
  // ===========================================================================

  /** Mapeia o erro HTTP para uma mensagem amigavel (403 = apenas master). */
  private tratarErro(err: any, chaveDefault: string): void {
    const detail =
      err?.status === 403
        ? this.translate.instant('configuracao-geral.texts.erro_apenas_master')
        : err?.error?.message || this.translate.instant(chaveDefault);

    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('common.erro'),
      detail,
    });
  }
}
